import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient, useApiRevalidate } from "../../../services/api";
import { displayToast } from "../../../components/app/AppToast";
import { memorizePostLoginRedirectPath } from "../../app/utils/postLoginRedirect";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useFirebaseUser } from "../../user/hooks/useFirebaseUser";

/**
 * Self-add flow triggered by scanning a playlist QR code.
 *
 * The QR encodes `/playlist/public/:id?shared=:ownerId&claim=1`. The `claim=1`
 * flag distinguishes a QR scan (auto-add to "My shared playlists") from a plain
 * shared link (consultation only). When present:
 * - logged out → memorize the URL (incl. `claim=1`) and bounce to /login; the
 *   claim resumes once the user returns authenticated.
 * - logged in → POST the self-add, then strip `claim` so a reload/back doesn't
 *   re-trigger it.
 */
export function useClaimSharedPlaylist(playlistId: string) {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { firebaseUser, isLoadingFirebaseUser } = useFirebaseUser();
  const revalidate = useApiRevalidate();
  const claimedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("claim") !== "1") return;
    if (playlistId === "") return;
    if (isLoadingFirebaseUser) return;
    if (claimedRef.current) return;

    if (!firebaseUser) {
      displayToast(t("✋ Please login to add this playlist"), "warning");
      memorizePostLoginRedirectPath();
      history.replace("/login" + globalThis.location.search);
      return;
    }

    // Authenticated via Firebase, but the api user doc isn't hydrated yet.
    if (currentUser === null) return;

    claimedRef.current = true;
    void (async () => {
      try {
        await apiClient.post("/webapp/private/playlists/shared-with-me", {
          json: { playlistId },
        });
        await revalidate("/webapp/private/playlists/shared-with-me");
        // The self-add pushed the user into the playlist's sharedWithUserIds
        // server-side; refresh the public playlist query so the page recomputes
        // `isSharedWithMe` (and hides the now-redundant save action).
        await revalidate("/webapp/public/playlists/:playlistId", {
          param: { playlistId },
        });

        const url = new URL(globalThis.location.href);
        url.searchParams.delete("claim");
        history.replace(url.pathname + url.search);

        displayToast(t("Added to your shared collections"), "success");
      } catch (error) {
        claimedRef.current = false;
        console.warn("Couldn't add shared playlist.", error);
      }
    })();
  }, [
    currentUser,
    firebaseUser,
    isLoadingFirebaseUser,
    playlistId,
    history,
    revalidate,
    t,
  ]);
}
